import { ArrowDownTrayIcon, ChartBarSquareIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { SelectDropdown } from "../../components/SelectDropdown";
import { getRangeForType } from "../../helpers/profitAndLoss/common";
import { MonthPickerInput } from "../../components/MonthPickerInput";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../../consts/general";
import ReactTooltip from "react-tooltip";
import { BalanceSheetLoadingKeys, getBalanceSheetData } from "../../slices/quickbooks/balanceSheetSlice";
import { BalanceSheetCard } from "../../components/quickbooks/BalanceSheetCard";
import { BalanceSheetCapitalRow } from "../../components/quickbooks/BalanceSheetCapitalRow";
import { isTotalRow, getTotalType, renderTotalRow, renderNode } from "../../helpers/quickbooks/balanceSheet";

const BalanceSheet = () => {

    const navigate = useNavigate();
    const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
    const dispatch = useDispatch();

    const [isExporting, setIsExporting] = useState(false);
    const [type, setType] = useState(pageParams?.get('type') || "last-month");
    const [compareType, setCompareType] = useState(pageParams?.get('compareType') || "none");
    const [valuesType, setValuesType] = useState(pageParams?.get('valuesType') || "months"); // Default to months
    const [startMonth, setStartMonth] = useState(pageParams?.get('startMonth') || "");
    const [endMonth, setEndMonth] = useState(pageParams?.get('endMonth') || "");

    const { balanceSheetData, loadingKeys: balanceSheetLoadingKeys } = useSelector((state) => state.balanceSheet || {});
    const balanceSheetIsLoading = balanceSheetLoadingKeys.includes(BalanceSheetLoadingKeys.getBalanceSheetData);

    useEffect(() => {
        if (!startMonth || !endMonth) return;
        const compareTypeIsNotEmpty = compareType && compareType !== "none";
        const params = new URLSearchParams({
          type,
          startMonth,
          endMonth,
          compareType: compareTypeIsNotEmpty ? compareType : "",
          valuesType,
        });
        dispatch(getBalanceSheetData({
            startMonth,
            endMonth,
            compareType: compareTypeIsNotEmpty ? compareType : null,
            valuesType,
        }));
        navigate(`${ROUTES.BALANCE_SHEET}?${params}`, { replace: true });
    }, [
        type,
        startMonth,
        endMonth,
        compareType,
        valuesType
    ]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [balanceSheetData]);

    const hasUrlDateParams = useMemo(() => {
        const hasType = !!pageParams?.get("type");
        const hasStart = !!pageParams?.get("startMonth");
        const hasEnd = !!pageParams?.get("endMonth");
        return hasType || hasStart || hasEnd;
    }, [pageParams]);

    useEffect(() => {
        if (hasUrlDateParams) return;
        if (type !== "last-month") return;
        if (startMonth || endMonth) return;
        const range = getRangeForType("last-month");
        if (range) {
          // Convert date range to month range
          const startMonthStr = range.start.slice(0, 7); // YYYY-MM
          const endMonthStr = range.end.slice(0, 7); // YYYY-MM
          setStartMonth(startMonthStr);
          setEndMonth(endMonthStr);
        }
    }, []);

    const applyType = (nextType) => {
        setType(nextType);
        if (nextType === "custom-dates") {
            return;
        }
        const range = getRangeForType(nextType);
        if (range) {
          // Convert date range to month range
          const startMonthStr = range.start.slice(0, 7); // YYYY-MM
          const endMonthStr = range.end.slice(0, 7); // YYYY-MM
          setStartMonth(startMonthStr);
          setEndMonth(endMonthStr);
        }
    };

    const handleStartMonthChange = (nextStart) => {
        const minMonth = "2024-01";
        const clamped = nextStart && nextStart < minMonth ? minMonth : nextStart;
        setStartMonth(clamped || "");
        if (endMonth && nextStart && nextStart > endMonth) {
          setEndMonth(clamped || "");
        }
    };

    const handleEndMonthChange = (nextEnd) => {
        const minMonth = "2024-01";
        const clamped = nextEnd && nextEnd < minMonth ? minMonth : nextEnd;
        setEndMonth(clamped || "");
        if (startMonth && nextEnd && nextEnd < startMonth) {
          setStartMonth(clamped || "");
        }
    };

    const handleExportAll = async () => {
        setIsExporting(true);
        try {
            // const response = await axios.get(`/api/v1/balance-sheet/export`, {
            //   params: {
            //     startDate,
            //     endDate,
            //     valuesType,
            //   },
            //   responseType: "blob",
            // });
            // const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
            // const url = window.URL.createObjectURL(blob);
            // const a = document.createElement("a");
            // a.href = url;
            // a.download = `balance_sheet_${valuesType}_${startDate}_to_${endDate}.csv`;
            // document.body.appendChild(a);
            // a.click();
            // a.remove();
            // window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to export:", error);
        } finally {
            setIsExporting(false);
        }
    }

    const [expanded, setExpanded] = useState({});

    const formatMoney = (n) => {
      const v = Number.isFinite(n) ? n : 0;
      const abs = Math.abs(v);
      const s = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(abs);
      return v < 0 ? `($${s})` : `$${s}`;
    };

    const getValueTextClass = (value, accentTextClass) => {
      const v = Number.isFinite(value) ? value : 0;
      if (v < 0) return "text-red-700";
      return accentTextClass || "text-gray-900";
    };

    const toggleExpanded = (id) => {
      setExpanded((prev) => ({ ...prev, [id]: !prev?.[id] }));
    };

    const getRowTotal = (node, columns) => {
      const cols = Array.isArray(columns) ? columns : [];
      const keys = cols.map((c) => c?.key).filter(Boolean);
      const nonTotal = keys.filter((k) => k !== "TOTAL");
      if (nonTotal.length > 0) {
        return nonTotal.reduce((acc, k) => acc + (Number.isFinite(node?.values?.[k]) ? node.values[k] : 0), 0);
      }
      return Number.isFinite(node?.values?.TOTAL) ? node.values.TOTAL : 0;
    };

    const getCellNumber = (node, colKey, columns) => {
      if (colKey === "TOTAL") return getRowTotal(node, columns);
      return Number.isFinite(node?.values?.[colKey]) ? node.values[colKey] : 0;
    };
 
    const displayColumns = valuesType === "total" 
      ? (balanceSheetData?.columns || [])
      : (balanceSheetData?.columns || []).filter(c => c?.key !== 'TOTAL');
    const assetsData = balanceSheetData?.assets || [];
    const liabilitiesData = balanceSheetData?.liabilitiesAndEquity || [];
    const financialMetrics = balanceSheetData?.financialMetrics || {};
    const financialMetricsCompare = balanceSheetData?.financialMetricsCompare || null;
    const workingCapitalRatios = balanceSheetData?.workingCapitalRatios || {};
    const conversionCycle = balanceSheetData?.conversionCycle || {};
    const conversionCycleCompare = balanceSheetData?.conversionCycleCompare || null;
    const compareLabel =
        compareType === "previous-year" ? "Previous Year" : compareType === "previous-period" ? "Previous Period" : null;

    return (
        <main className="w-full px-8">
            <div className="h-auto py-6 flex flex-row gap-4 justify-between items-center">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                                <ChartBarSquareIcon className="h-6 w-6 text-white"/>
                            </div>
                            <h1 className="text-xl font-semibold text-gray-900">KINGS LOOT</h1>
                        </div>
                        <div className="bg-green-100 text-green-800 border-green-200 px-4 py-0.5 rounded-full font-bold text-sm whitespace-nowrap">
                            Balance Sheet Dashboard
                        </div>
                    </div>
                </div>
                {/* <button 
                  onClick={handleExportAll}
                  disabled={isExporting}
                  className="flex bg-primary transition-all duration-200 rounded-lg justify-between gap-2 items-center text-white text-sm rounded-lg outline-none p-2.5 px-3 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isExporting ? (
                        <>
                            <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
                            <span>Exporting...</span>
                        </>
                    ) : (
                        <>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Export All</span>
                        </>
                    )}
                </button> */}
            </div>
            <div className="h-auto py-0 pb-0 flex flex-col">
              <div className="flex flex-row items-center gap-3 flex-wrap">
                {(
                  <>
                    <div className="flex flex-row gap-2 items-center">
                      <span>Reporting Period:</span>
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "this-year-to-date", label: "This Year to Date" },
                            { value: "last-month", label: "Last Month" },
                            { value: "last-quarter", label: "Last Quarter" },
                            { value: "last-year", label: "Last Year" },
                            { value: "custom-dates", label: "Custom Dates" },
                          ]}
                          value={type}
                          onChange={applyType}
                          dropdownClassname={"!z-50"}
                          selectClassname={"!bg-white !p-2"}
                          disabled={balanceSheetIsLoading}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                         <div className="w-52">
                            <MonthPickerInput
                              value={startMonth}
                              onChange={handleStartMonthChange}
                              placeholder="Start month"
                              min="2024-01"
                              max={endMonth || undefined}
                              disabled={balanceSheetIsLoading}
                            />
                          </div>
                        </div>
                        <span>to</span>
                        <div className="flex flex-row gap-2 items-center">
                          <div className="w-52">
                            <MonthPickerInput
                              value={endMonth}
                              onChange={handleEndMonthChange}
                              placeholder="End month"
                              min={(startMonth && startMonth > "2024-01") ? startMonth : "2024-01"}
                              disabled={balanceSheetIsLoading}
                            />
                        </div>
                    </div>
                    <span>Values</span>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "total", label: "Total Only" },
                            { value: "months", label: "By Month"},
                            { value: "quarters", label: "By Quarter"},
                          ]}
                          value={valuesType}
                          onChange={setValuesType}
                          dropdownClassname={"!z-50"}
                          selectClassname={"!bg-white !p-2"}
                          disabled={balanceSheetIsLoading}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <span>Compare:</span>
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "none", label: "No Compare" },
                            { value: "previous-period", label: "Previous Period" },
                            { value: "previous-year", label: "Previous Year" },
                          ]}
                          value={compareType}
                          onChange={setCompareType}
                          selectClassname={"!bg-white"}
                          disabled={balanceSheetIsLoading}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {balanceSheetIsLoading ? (
                <div className="flex justify-center items-center h-full w-full mt-10">
                    <LoaderSpinner height="100" width="100" border="5" colorSpinner="black" />
                </div>
            ) : (
                <>
                    <section className="h-auto py-6 flex flex-col gap-4">
                        <h4 className="text-xl font-semibold">Financial Health Overview</h4>
                        <div className="flex flex-wrap gap-4">
                            <BalanceSheetCard  
                                type="cash"
                                cardTitle="Cash on Hand"
                                cardPrice={financialMetrics?.cashOnHand || "$0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.cashOnHand || "$0.00") : null}
                                compareLabel={compareLabel}
                                // cardPercentageRatio=""
                                // cardPercentage={cashRatio.TOTAL || "0.00%"}
                            />
                            <BalanceSheetCard  
                                type="inventory"
                                cardTitle="Inventory Value"
                                cardPrice={financialMetrics?.inventoryValue || "$0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.inventoryValue || "$0.00") : null}
                                compareLabel={compareLabel}
                                // cardPercentageRatio=""
                                // cardPercentage={"0.00%"}
                            />
                            <BalanceSheetCard  
                                type="capital"
                                cardTitle="Working Capital"
                                cardPrice={financialMetrics?.workingCapital || "$0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.workingCapital || "$0.00") : null}
                                compareLabel={compareLabel}
                                // cardPercentageRatio={workingCapitalRatio.TOTAL || "0.00"}
                                // cardPercentage={workingCapitalRatio.TOTAL || "0.00%"}
                            />
                            <BalanceSheetCard  
                                type="current-ratio"
                                cardTitle="Current Ratio"
                                cardPrice={financialMetrics?.currentRatio || "0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.currentRatio || "0.00") : null}
                                compareLabel={compareLabel}
                                comparePercentage={compareLabel ? (financialMetricsCompare?.currentRatioFlag || "") : null}
                                cardPercentageRatio=""
                                cardPercentage={financialMetrics?.currentRatioFlag || "Strong"}
                            />
                            <BalanceSheetCard  
                                type="quick-ratio"
                                cardTitle="Quick Ratio"
                                cardPrice={financialMetrics?.quickRatio || "0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.quickRatio || "0.00") : null}
                                compareLabel={compareLabel}
                                comparePercentage={compareLabel ? (financialMetricsCompare?.quickRatioFlag || "") : null}
                                cardPercentageRatio=""
                                cardPercentage={financialMetrics?.quickRatioFlag || "Strong"}
                            />
                            <BalanceSheetCard  
                                type="profit"
                                cardTitle="Debt/TTM Profit"
                                cardPrice={financialMetrics?.debtTTMProfit || "$0.00"}
                                comparePrice={compareLabel ? (financialMetricsCompare?.debtTTMProfit || "$0.00") : null}
                                compareLabel={compareLabel}
                                comparePercentage={compareLabel ? (financialMetricsCompare?.debtTTMProfitFlag || "") : null}
                                cardPercentageRatio=""
                                cardPercentage={financialMetrics?.debtTTMProfitFlag || ""}
                            />
                        </div>
                    </section>
                    {/* <section className="h-auto py-6 flex flex-col gap-4">
                        <div className="rounded-lg border-2 shadow-md bg-gray-50/50">
                            <div className="flex flex-col space-y-1.5 p-6 border-b bg-gray-100/50">
                                <h4 className="text-lg font-semibold text-gray-900">Working Capital Insights & Ratios</h4>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-wrap gap-8">
                                    <div className="space-y-4 flex-1">
                                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b pb-2">
                                            Liquidity Ratios
                                        </h3>
                                        <BalanceSheetCapitalRow
                                            label="Current Ratio"
                                            value={workingCapitalRatios.currentRatio || "0.00"}
                                            tooltipText="<strong>Current Ratio = Current Assets / Current Liabilities</strong> <br /> Measures ability to pay short-term obligations. A ratio <br /> above 2.0 is considered strong."
                                        />
                                        <BalanceSheetCapitalRow
                                            label="Quick Ratio"
                                            value={workingCapitalRatios.quickRatio || "0.00"}
                                            tooltipText="<strong>Quick Ratio = (Current Assets - Inventory) / Current Liabilities</strong> <br /> More conservative liquidity measure. A ratio <br /> above 1.0 is generally healthy."
                                        />
                                        <BalanceSheetCapitalRow
                                            label="Cash Ratio"
                                            value={workingCapitalRatios.cashRatio || "0.00"}
                                            tooltipText="<strong>Cash Ratio = Bank Accounts / Current Liabilities</strong> <br /> Most stringent liquidity measure. Shows immediate <br /> debt-paying ability."
                                        />
                                    </div>
                                    <div className="space-y-4 flex-1">
                                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b pb-2">
                                            Cash Position
                                        </h3>
                                        <BalanceSheetCapitalRow
                                            label="Cash & Equivalents"
                                            value={workingCapitalRatios.cashEquivalents || "$0.00"}
                                        />
                                        <BalanceSheetCapitalRow
                                            label="Accounts Receivable"
                                            value={workingCapitalRatios.accountsReceivable || "$0.00"}
                                        />
                                        <BalanceSheetCapitalRow
                                            label="% of Current Assets"
                                            value={workingCapitalRatios.percentageOfCurrentAssets || "0.00%"}
                                            tooltipText="<strong>(Cash & Equivalents / Current Assets) x 100</strong> <br /> Shows what percentage of current assets is held in <br /> cash. Higher percentage means more liquidity."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section> */}
                    <section className="h-auto py-6 flex flex-col gap-4">
                        <div className="rounded-lg border-2 shadow-md bg-gray-50/50">
                            <div className="flex flex-col space-y-1.5 p-6 border-b bg-gray-100/50">
                                <h4 className="text-lg font-semibold text-gray-900">Balance Sheet Summary</h4>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-wrap gap-8">
                                    <div className="space-y-4 flex-1 min-w-0">
                                        <h3 className="border-b-2 border-green-300 pb-3 text-lg font-bold text-green-800">
                                            Assets          
                                        </h3>
                                        <section>
                                            {balanceSheetIsLoading ? (
                                                <div className="py-8 flex justify-center">
                                                    <LoaderSpinner width="70" height="70" border="5" colorSpinner="black" />
                                                </div>
                                            ) : (
                                                <div className="rounded-lg border border-gray-200 bg-white p-6">
                                                    <div className="overflow-auto max-h-[calc(100vh-300px)]">
                                                        <div className={valuesType === "total" ? "w-full" : "min-w-max"}>
                                                            <div
                                                                className="sticky top-0 z-30 grid bg-white border-b border-gray-200"
                                                                style={{
                                                                    gridTemplateColumns: (() => {
                                                                        const desc =
                                                                            valuesType === "quarters"
                                                                                ? "560px"
                                                                                : valuesType === "total"
                                                                                ? "minmax(320px, 1fr)"
                                                                                : "minmax(320px, 2fr)";

                                                                        const periodCols = displayColumns.filter((c) => c?.key !== "TOTAL");
                                                                        const periodTemplate = valuesType === "quarters" ? "minmax(140px, 1fr)" : "140px";
                                                                        const middle = periodCols.length > 0 ? `repeat(${periodCols.length}, ${periodTemplate})` : "";
                                                                        const total = valuesType === "quarters" ? "180px" : "140px";
                                                                        const hasTotal = displayColumns.some((c) => c?.key === "TOTAL");

                                                                        return `${desc} ${middle} ${hasTotal ? total : ""}`.trim();
                                                                    })(),
                                                                }}
                                                            >
                                                                <div
                                                                    className={[
                                                                        "text-left text-xs font-semibold text-gray-600 py-2 px-3 bg-white relative",
                                                                        valuesType !== "total" ? "sticky left-0 z-40" : "",
                                                                    ].join(" ")}
                                                                >
                                                                    {valuesType !== "total" ? <div className="absolute top-0 right-0 h-full w-px bg-gray-200" /> : null}
                                                                    <div className="relative">DESCRIPTION</div>
                                                                </div>

                                                                {displayColumns.filter((c) => c?.key !== "TOTAL").map((c, idx) => {
                                                                    const isFirstInGroup = c?.key?.startsWith('CUR:');
                                                                    const isNotFirstGroup = idx > 0 && isFirstInGroup;
                                                                    return (
                                                                        <div
                                                                            key={c.key}
                                                                            className={[
                                                                                "text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap",
                                                                                isNotFirstGroup ? "border-l-2 border-gray-300" : ""
                                                                            ].join(" ")}
                                                                        >
                                                                            {String(c.label || c.key).toUpperCase()}
                                                                        </div>
                                                                    );
                                                                })}

                                                                {displayColumns.some((c) => c?.key === "TOTAL") ? (
                                                                    <div className={[
                                                                        "text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap bg-white relative",
                                                                        valuesType !== "total" ? "sticky right-0 z-40" : "",
                                                                    ].join(" ")}>
                                                                        {valuesType !== "total" ? <div className="absolute top-0 left-0 h-full w-px bg-gray-200" /> : null}
                                                                        <div className="relative">TOTAL</div>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                            <div className="space-y-3">
                                                                {(assetsData || []).map((item) => {
                                                                    const parent = item?.parent;
                                                                    const childrens = item?.childrens || [];
                                                                    if (!parent) return null;

                                                                    // Skip first level - render children directly
                                                                    const topLevelChildren = childrens || [];
                                                                    
                                                                    // Separate totals from regular children
                                                                    const totalRows = topLevelChildren.filter(child => isTotalRow(child));
                                                                    const nonTotalChildren = topLevelChildren
                                                                    .filter(child => !isTotalRow(child))
                                                                    .sort((a, b) => {
                                                                        const posA = Number.isFinite(a?.position) ? a.position : 999999;
                                                                        const posB = Number.isFinite(b?.position) ? b.position : 999999;
                                                                        return posA - posB;
                                                                    });
                                                                    
                                                                    return (
                                                                        <React.Fragment key={parent.id}>
                                                                            {nonTotalChildren.map((child) => (
                                                                                <div key={child.id}>
                                                                                    {renderNode(child, 0, displayColumns, 'assets', valuesType, expanded, toggleExpanded, formatMoney, getValueTextClass, getCellNumber)}
                                                                                </div>
                                                                            ))}
                                                                            {/* Render Total Assets after all children */}
                                                                            {totalRows.map((totalRow) => (
                                                                                <div key={totalRow.id} className="mt-3">
                                                                                    {renderTotalRow(totalRow, displayColumns, 'assets', valuesType, formatMoney, getCellNumber, getValueTextClass)}
                                                                                </div>
                                                                            ))}
                                                                            {/* Also check if parent itself is Total Assets */}
                                                                            {isTotalRow(parent) && getTotalType(parent) === 'totalAssets' && !totalRows.length && (
                                                                                <div className="mt-3">
                                                                                    {renderTotalRow(parent, displayColumns, 'assets', valuesType, formatMoney, getCellNumber, getValueTextClass)}
                                                                                </div>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </section>
                                    </div>
                                    <div className="space-y-4 flex-1 min-w-0">
                                        <h3 className="border-b-2 border-red-300 pb-3 text-lg font-bold text-red-800">
                                            Liabilities & Equity
                                        </h3>
                                        <section>
                                            {balanceSheetIsLoading ? (
                                                <div className="py-8 flex justify-center">
                                                    <LoaderSpinner width="70" height="70" border="5" colorSpinner="black" />
                                                </div>
                                            ) : (
                                                <div className="rounded-lg border border-gray-200 bg-white p-6">
                                                    <div className="overflow-auto max-h-[calc(100vh-300px)]">
                                                        <div className={valuesType === "total" ? "w-full" : "min-w-max"}>
                                                            <div
                                                                className="sticky top-0 z-30 grid bg-white border-b border-gray-200"
                                                                style={{
                                                                    gridTemplateColumns: (() => {
                                                                        const desc =
                                                                            valuesType === "quarters"
                                                                                ? "560px"
                                                                                : valuesType === "total"
                                                                                ? "minmax(320px, 1fr)"
                                                                                : "minmax(320px, 2fr)";

                                                                        const periodCols = displayColumns.filter((c) => c?.key !== "TOTAL");
                                                                        const periodTemplate = valuesType === "quarters" ? "minmax(140px, 1fr)" : "140px";
                                                                        const middle = periodCols.length > 0 ? `repeat(${periodCols.length}, ${periodTemplate})` : "";
                                                                        const total = valuesType === "quarters" ? "180px" : "140px";
                                                                        const hasTotal = displayColumns.some((c) => c?.key === "TOTAL");

                                                                        return `${desc} ${middle} ${hasTotal ? total : ""}`.trim();
                                                                    })(),
                                                                }}
                                                            >
                                                                <div
                                                                    className={[
                                                                        "text-left text-xs font-semibold text-gray-600 py-2 px-3 bg-white relative",
                                                                        valuesType !== "total" ? "sticky left-0 z-40" : "",
                                                                    ].join(" ")}
                                                                >
                                                                    {valuesType !== "total" ? <div className="absolute top-0 right-0 h-full w-px bg-gray-200" /> : null}
                                                                    <div className="relative">DESCRIPTION</div>
                                                                </div>

                                                                {displayColumns.filter((c) => c?.key !== "TOTAL").map((c, idx) => {
                                                                    const isFirstInGroup = c?.key?.startsWith('CUR:');
                                                                    const isNotFirstGroup = idx > 0 && isFirstInGroup;
                                                                    return (
                                                                        <div
                                                                            key={c.key}
                                                                            className={[
                                                                                "text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap",
                                                                                isNotFirstGroup ? "border-l-2 border-gray-300" : ""
                                                                            ].join(" ")}
                                                                        >
                                                                            {String(c.label || c.key).toUpperCase()}
                                                                        </div>
                                                                    );
                                                                })}

                                                                {displayColumns.some((c) => c?.key === "TOTAL") ? (
                                                                    <div className={[
                                                                        "text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap bg-white relative",
                                                                        valuesType !== "total" ? "sticky right-0 z-40" : "",
                                                                    ].join(" ")}>
                                                                        {valuesType !== "total" ? <div className="absolute top-0 left-0 h-full w-px bg-gray-200" /> : null}
                                                                        <div className="relative">TOTAL</div>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                            <div className="space-y-3">
                                                                {(liabilitiesData || []).map((item) => {
                                                                    const parent = item?.parent;
                                                                    const childrens = item?.childrens || [];
                                                                    if (!parent) return null;

                                                                    // Skip first level - render children directly
                                                                    const topLevelChildren = childrens || [];
                                                                    
                                                                    // Find totals in children (Total Liabilities, Total Equity, Total Liabilities + Equity)
                                                                    // Sort totals: Total Liabilities first, then Total Equity, then Total Liabilities + Equity
                                                                    const totalRows = topLevelChildren
                                                                    .filter(child => isTotalRow(child))
                                                                    .sort((a, b) => {
                                                                        const typeA = getTotalType(a);
                                                                        const typeB = getTotalType(b);
                                                                        const order = { totalLiabilities: 1, totalEquity: 2, totalLiabilitiesAndEquity: 3 };
                                                                        return (order[typeA] || 999) - (order[typeB] || 999);
                                                                    });
                                                                    const nonTotalChildren = topLevelChildren
                                                                    .filter(child => !isTotalRow(child))
                                                                    .sort((a, b) => {
                                                                        const posA = Number.isFinite(a?.position) ? a.position : 999999;
                                                                        const posB = Number.isFinite(b?.position) ? b.position : 999999;
                                                                        return posA - posB;
                                                                    });
                                                                    
                                                                    return (
                                                                        <React.Fragment key={parent.id}>
                                                                            {nonTotalChildren.map((child) => (
                                                                                <div key={child.id}>
                                                                                    {renderNode(child, 0, displayColumns, 'liabilitiesAndEquity', valuesType, expanded, toggleExpanded, formatMoney, getValueTextClass, getCellNumber)}
                                                                                </div>
                                                                            ))}
                                                                            {/* Render total rows after all children in correct order */}
                                                                            {totalRows.map((totalRow) => (
                                                                                <div key={totalRow.id} className="mt-3">
                                                                                    {renderTotalRow(totalRow, displayColumns, 'liabilitiesAndEquity', valuesType, formatMoney, getCellNumber, getValueTextClass)}
                                                                                </div>
                                                                            ))}
                                                                            {/* Also check if parent itself is a total (but not if we already rendered it) */}
                                                                            {isTotalRow(parent) && !totalRows.some(tr => getTotalType(tr) === getTotalType(parent)) && (
                                                                                <div className="mt-3">
                                                                                    {renderTotalRow(parent, displayColumns, 'liabilitiesAndEquity', valuesType, formatMoney, getCellNumber, getValueTextClass)}
                                                                                </div>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </section>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="h-auto py-6 flex flex-col gap-4">
                        <div className="rounded-lg border-2 shadow-md bg-gray-50/50">
                            <div className="flex flex-col space-y-1.5 p-6 border-b bg-gray-100/50">
                                <h4 className="text-lg font-semibold text-gray-900">Cash Conversion Cycle</h4>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-col gap-4">
                                    <div className="rounded-lg bg-slate-50 p-3 text-center text-sm text-slate-500 border border-dashed">
                                        Requires trailing 12-month Sales & COGS data to calculate cycle metrics
                                    </div>
                                    <div className="flex flex-col lg:flex-row items-stretch gap-4">
                                        <div className="rounded-lg flex-1 border-2 border-blue-200 bg-white shadow-sm flex flex-col">
                                            <div className="flex flex-col space-y-1.5 p-6">
                                                <div className="flex items-center justify-between">
                                                    <h6 className="tracking-tight text-base font-semibold text-gray-800">
                                                        Sellable CCC
                                                    </h6>
                                                    <InformationCircleIcon
                                                        className="h-4 w-4 text-gray-500"
                                                        data-tip={true}
                                                        data-for="sellable_ccc_tooltip"
                                                    />
                                                    <ReactTooltip
                                                        id="sellable_ccc_tooltip"
                                                        html={true}
                                                    >
                                                        {`
                                                            <div>
                                                                <strong>Sellable CCC = DSO + DIO (Vegas) - DPO</strong>
                                                                <br />
                                                                Cash conversion using only Vegas/sellable inventory. 
                                                                <br />
                                                                Lower is better.
                                                            </div>
                                                        `}
                                                    </ReactTooltip>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    Vegas / Sellable Inventory Only
                                                </p>
                                            </div>
                                            <div className="p-6 flex-1 flex flex-col gap-4">
                                                <div className="text-center">
                                                    <span className="text-4xl font-bold tabular-nums text-blue-700">
                                                        {conversionCycle.sellableCCC?.ccc || "—"}
                                                    </span>
                                                    <span className="ml-2 text-lg text-gray-500">
                                                        days
                                                    </span>
                                                </div>
                                                <div className="space-y-3 flex-1">
                                                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                                                        Cycle Composition
                                                    </p>
                                                    <div className="relative h-2.5 rounded-full overflow-hidden bg-gray-200">
                                                        <div className="h-full w-full bg-gray-200" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-blue-400" />
                                                                DSO
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.sellableCCC?.dso || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
                                                                DIO (Sellable)
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.sellableCCC?.dio || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
                                                                DPO (offset)
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.sellableCCC?.dpo || "—"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-500 text-center pt-1 border-t">
                                                        DSO + DIO - DPO = CCC
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-3 border-t border-blue-100">
                                                    <p className="text-xs text-blue-600 text-center font-medium">
                                                        Operational inventory efficiency
                                                    </p>
                                                </div>
                                                {compareType !== "none" ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="h-0.5 w-full bg-blue-200" />
                                                        <div className="text-center">
                                                            {compareLabel ? (
                                                                <div className="text-xs font-semibold text-blue-700 mb-1">
                                                                    {compareLabel}
                                                                </div>
                                                            ) : null}
                                                            <span className="text-4xl font-bold tabular-nums text-blue-700">
                                                                {conversionCycleCompare?.sellableCCC?.ccc || "—"}
                                                            </span>
                                                            <span className="ml-2 text-lg text-gray-500">
                                                                days
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="hidden lg:flex items-center justify-center">
                                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                <span className="text-xl font-bold text-slate-600">
                                                    +
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rounded-lg flex-1 border-2 border-amber-200 bg-white shadow-sm flex flex-col">
                                            <div className="flex flex-col space-y-1.5 p-6">
                                                <div className="flex items-center justify-between">
                                                    <h6 className="tracking-tight text-base font-semibold text-gray-800">
                                                        Pipeline Days
                                                    </h6>
                                                    <InformationCircleIcon
                                                        className="h-4 w-4 text-gray-500"
                                                        data-tip={true}
                                                        data-for="pipeline_days_tooltip"
                                                    />
                                                    <ReactTooltip
                                                        id="pipeline_days_tooltip"
                                                        html={true}
                                                    >
                                                        {`
                                                            <div>
                                                                <strong>Pipeline Days = (China Inventory / COGS TTM) x 365</strong>
                                                                <br />
                                                                Days of inventory in-transit from China. Represents
                                                                <br />
                                                                cash tied up in pipeline.
                                                            </div>
                                                        `}
                                                    </ReactTooltip>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    China / In-Transit Inventory
                                                </p>
                                            </div>
                                            <div className="p-6 flex-1 flex flex-col gap-4">
                                                <div className="text-center">
                                                    <span className="text-4xl font-bold tabular-nums text-amber-600">
                                                        {conversionCycle.pipelineDays?.ccc || "—"}
                                                    </span>
                                                    <span className="ml-2 text-lg text-gray-500">
                                                        days
                                                    </span>
                                                </div>
                                                <div className="space-y-3 flex-1">
                                                    <div className="rounded-lg bg-amber-50 p-3 border border-amber-100">
                                                        <div className="flex items-center justify-between text-sm">
                                                            <span className="text-amber-700">
                                                                Pipeline Inventory Value
                                                            </span>
                                                            <span className="font-semibold tabular-nums text-amber-800">
                                                                {conversionCycle.pipelineDays?.pipelineInventoryValue || "$0.00"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-500 space-y-1">
                                                        <div className="flex items-center justify-between">
                                                            <span>
                                                                Avg Transit Time
                                                            </span>
                                                            <span className="font-medium text-gray-700">
                                                                {conversionCycle.pipelineDays?.avgTransitTime ? `~${conversionCycle.pipelineDays?.avgTransitTime} days` : "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span>
                                                                % of Total Inventory
                                                            </span>
                                                            <span className="font-medium text-gray-700">
                                                                {conversionCycle.pipelineDays?.percentageOfInventoryValue ? `~${conversionCycle.pipelineDays?.percentageOfInventoryValue}` : "—"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-3 border-t border-amber-100">
                                                    <p className="text-xs text-amber-600 text-center font-medium">
                                                        How long cash is stuck before goods sellable
                                                    </p>
                                                </div>
                                                {compareType !== "none" ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="h-0.5 w-full bg-amber-200" />
                                                        <div className="text-center">
                                                            {compareLabel ? (
                                                                <div className="text-xs font-semibold text-amber-700 mb-1">
                                                                    {compareLabel}
                                                                </div>
                                                            ) : null}
                                                            <span className="text-4xl font-bold tabular-nums text-amber-600">
                                                                {conversionCycleCompare?.pipelineDays?.ccc || "—"}
                                                            </span>
                                                            <span className="ml-2 text-lg text-gray-500">
                                                                days
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="hidden lg:flex items-center justify-center">
                                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                                <span className="text-xl font-bold text-slate-600">
                                                    =
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rounded-lg flex-1 border-2 border-emerald-200 bg-white shadow-sm flex flex-col">
                                            <div className="flex flex-col space-y-1.5 p-6">
                                                <div className="flex items-center justify-between">
                                                    <h6 className="tracking-tight text-base font-semibold text-gray-800">
                                                    Total CCC
                                                    </h6>
                                                    <InformationCircleIcon
                                                        className="h-4 w-4 text-gray-500"
                                                        data-tip={true}
                                                        data-for="total_ccc_tooltip"
                                                    />
                                                    <ReactTooltip
                                                        id="total_ccc_tooltip"
                                                        html={true}
                                                    >
                                                        {`
                                                            <div>
                                                                <strong>Total CCC = DSO + DIO (All) - DPO</strong>
                                                                <br />
                                                                Full cash conversion including sellable + pipeline 
                                                                <br />
                                                                inventory. Lower is better.
                                                            </div>
                                                        `}
                                                    </ReactTooltip>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    Sellable + Pipeline Inventory
                                                </p>
                                            </div>
                                            <div className="p-6 flex-1 flex flex-col gap-4">
                                                <div className="text-center">
                                                    <span className="text-4xl font-bold tabular-nums text-emerald-700">
                                                        {conversionCycle.totalCCC?.ccc || "—"}
                                                    </span>
                                                    <span className="ml-2 text-lg text-gray-500">
                                                        days
                                                    </span>
                                                </div>
                                                <div className="space-y-3 flex-1">
                                                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                                                        Cycle Composition
                                                    </p>
                                                    <div className="relative h-2.5 rounded-full overflow-hidden bg-gray-200">
                                                        <div className="h-full w-full bg-gray-200" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-blue-400" />
                                                                DSO
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.totalCCC?.dso || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
                                                                DIO (Sellable)
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.sellableCCC?.dio || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-orange-400" />
                                                                DIO (Pipeline)
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.pipelineDays?.dio || "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="flex items-center gap-1.5 text-gray-600">
                                                                <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
                                                                DPO (offset)
                                                            </span>
                                                            <span className="font-medium tabular-nums text-gray-800">
                                                                {conversionCycle.totalCCC?.dpo || "—"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-500 text-center pt-1 border-t">
                                                        DSO + DIO (All) - DPO = CCC
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-3 border-t border-emerald-100">
                                                    <p className="text-xs text-emerald-600 text-center font-medium">
                                                    Working-capital / Cash reality
                                                    </p>
                                                </div>
                                                {compareType !== "none" ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="h-0.5 w-full bg-emerald-200" />
                                                        <div className="text-center">
                                                            {compareLabel ? (
                                                                <div className="text-xs font-semibold text-emerald-700 mb-1">
                                                                    {compareLabel}
                                                                </div>
                                                            ) : null}
                                                            <span className="text-4xl font-bold tabular-nums text-emerald-700">
                                                                {conversionCycleCompare?.totalCCC?.ccc || "—"}
                                                            </span>
                                                            <span className="ml-2 text-lg text-gray-500">
                                                                days
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </main>
    );
};

export default BalanceSheet;